import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModalComponent, ModalConfig } from '../modal/modal';
import { ModalStatusComponent } from '../modal-status/modal-status';
import { AppLoaderComponent } from '../app-loader/app-loader';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@Component({
  selector: 'app-share-assessment-modal',
  imports: [
    FormsModule,
    MatIconModule,
    TranslocoModule,
    ModalComponent,
    ModalStatusComponent,
    AppLoaderComponent,
  ],
  templateUrl: './share-assessment-modal.html',
  styleUrl: './share-assessment-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShareAssessmentModalComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translationVersion = signal(0);

  constructor() {
    this.transloco.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.translationVersion.update(v => v + 1));
  }

  readonly isVisible = input(false);
  readonly isSharing = input(false);
  readonly errorMessage = input<string | null>(null);
  readonly successMessage = input<string | null>(null);

  readonly confirmed = output<{ emails: string[]; message: string }>();
  readonly cancelled = output<void>();
  readonly closed = output<void>();

  // Public so tests can manipulate / assert.
  readonly recipientEmails = signal('');
  readonly message = signal('');
  readonly internalError = signal<string | null>(null);

  // Combine local validation errors with parent-supplied async errors.
  readonly error = computed(() => this.internalError() ?? this.errorMessage());

  readonly isFormValid = computed(() => {
    const emails = this.parsedEmails();
    return emails.length > 0 && emails.every(e => EMAIL_REGEX.test(e));
  });

  readonly modalConfig = computed<ModalConfig>(() => {
    this.translationVersion();
    return {
      title: this.transloco.translate('modals.share_assessment.modal_title'),
      size: 'medium',
      showLogo: true,
      showCloseButton: !this.successMessage(),
      showFooter: !this.successMessage(),
      primaryButtonText: this.transloco.translate('modals.share_assessment.modal_primary'),
      secondaryButtonText: this.transloco.translate('common.cancel'),
    };
  });

  readonly primaryButtonDisabled = computed(() => this.isSharing() || !this.isFormValid());

  onModalConfirmed(): void {
    if (!this.isFormValid()) {
      this.internalError.set(this.transloco.translate('modals.share_assessment.invalid_emails'));
      return;
    }
    this.internalError.set(null);
    this.confirmed.emit({ emails: this.parsedEmails(), message: this.message() });
  }

  onModalCancelled(): void {
    this.resetForm();
    this.cancelled.emit();
  }

  onModalClosed(): void {
    this.resetForm();
    this.closed.emit();
  }

  onRecipientEmailsInput(event: Event): void {
    this.recipientEmails.set((event.target as HTMLInputElement).value);
    this.internalError.set(null);
  }

  onMessageInput(event: Event): void {
    this.message.set((event.target as HTMLTextAreaElement).value);
  }

  clearError(): void {
    this.internalError.set(null);
  }

  private parsedEmails(): string[] {
    return this.recipientEmails()
      .split(',')
      .map(e => e.trim())
      .filter(e => e.length > 0);
  }

  private resetForm(): void {
    this.recipientEmails.set('');
    this.message.set('');
    this.internalError.set(null);
  }
}
